import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  HiOutlineServer, HiOutlineCircleStack, HiOutlineEnvelope, HiOutlineGlobeAlt,
  HiOutlineArchiveBox, HiOutlineBriefcase, HiOutlineMagnifyingGlass, HiOutlineBeaker,
  HiOutlineWrenchScrewdriver, HiOutlineCpuChip, HiOutlineCloud, HiOutlineArrowPath,
  HiOutlineCube, HiOutlineBolt, HiOutlineRocketLaunch, HiOutlineDocumentText,
  HiOutlineComputerDesktop, HiOutlineShieldCheck, HiOutlineMapPin, HiOutlineCloudArrowUp,
  HiOutlineSignal, HiOutlineStar, HiOutlineChatBubbleLeftRight
} from 'react-icons/hi2';

/* ---- Scroll Reveal (Enhanced) ---- */
function RevealSection({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(e.target); } }, { threshold: 0.08 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`reveal-section ${visible ? 'revealed' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ---- Data ---- */
const solutions = [
  { icon: <HiOutlineServer size={28} />, title: 'Veri Merkezi', desc: 'İstanbul, Ankara ve İzmir\'deki Tier 3+ veri merkezlerinde güvenli barındırma', path: '/cozumler/veri-merkezi', color: 'blue' },
  { icon: <HiOutlineCircleStack size={28} />, title: 'Veritabanı', desc: 'KVKK uyumlu, yüksek performanslı veritabanı hizmetleri', path: '/hizmetler/veritabani', color: 'purple' },
  { icon: <HiOutlineEnvelope size={28} />, title: 'Mail Çözümleri', desc: 'Türkiye altyapısında çalışan kurumsal e-posta çözümleri', path: '/cozumler/mail', color: 'cyan' },
  { icon: <HiOutlineGlobeAlt size={28} />, title: 'Network', desc: 'Güvenli ağ bağlantısı ve SD-WAN çözümleri', path: '/cozumler/network', color: 'emerald' },
  { icon: <HiOutlineArchiveBox size={28} />, title: 'Yedekleme', desc: 'KVKK uyumlu, coğrafi yedekli yedekleme çözümleri', path: '/cozumler/yedekleme', color: 'amber' },
  { icon: <HiOutlineBriefcase size={28} />, title: 'Danışmanlık', desc: 'BT ihtiyaçlarınızın tespiti ve stratejik danışmanlık', path: '/hizmetler/danismanlik', color: 'teal' },
];

const processSteps = [
  { num: '01', icon: <HiOutlineMagnifyingGlass size={32} />, title: 'Detaylı Analiz', desc: 'Yüz yüze veya uzaktan ihtiyaç analizi yapıyoruz.' },
  { num: '02', icon: <HiOutlineBeaker size={32} />, title: 'Ar-Ge Hizmetleri', desc: 'En güncel çözümleri geliştiriyoruz.' },
  { num: '03', icon: <HiOutlineWrenchScrewdriver size={32} />, title: 'Altyapı Modelleme', desc: 'En uygun altyapıyı tasarlıyoruz.' },
  { num: '04', icon: <HiOutlineCpuChip size={32} />, title: 'Teknoloji Belirleme', desc: 'En iyi teknolojiyi belirliyoruz.' },
];

const featuredServices = [
  { icon: <HiOutlineCloudArrowUp size={32} />, title: 'Özel Bulut Sunucu', tag: 'Cloud', desc: 'Size özel izole bulut altyapısı ile tam kontrol ve maksimum güvenlik.', path: '/hizmetler/ozel-bulut', color: 'blue' },
  { icon: <HiOutlineRocketLaunch size={32} />, title: 'CDN Hizmetleri', tag: 'Performance', desc: 'Küresel dağıtım ağı ile milisaniye düzeyinde içerik teslimatı.', path: '/hizmetler/cdn', color: 'purple' },
  { icon: <HiOutlineSignal size={32} />, title: 'Network Hizmetleri', tag: 'Network', desc: 'Güvenli ve yönetilebilir kurumsal ağ altyapı çözümleri.', path: '/hizmetler/network', color: 'cyan' },
  { icon: <HiOutlineDocumentText size={32} />, title: 'Lisans Kiralama', tag: 'Lisans', desc: 'Yazılım lisanslarını esnek kiralama modeli ile kullanın.', path: '/hizmetler/lisans', color: 'emerald' },
  { icon: <HiOutlineComputerDesktop size={32} />, title: 'Donanım Kiralamak', tag: 'Hardware', desc: 'Enterprise donanımları CapEx yerine OpEx modeli ile edinin.', path: '/hizmetler/haas', color: 'amber' },
  { icon: <HiOutlineBriefcase size={32} />, title: 'Uzman Kiralamak', tag: 'Expert', desc: 'Sertifikalı BT uzmanlarını projenize dahil edin.', path: '/hizmetler/eaas', color: 'rose' },
  { icon: <HiOutlineCloud size={32} />, title: 'Bulut Hizmetleri', tag: 'Cloud', desc: 'Ölçeklenebilir, KVKK uyumlu bulut altyapı çözümleri.', path: '/hizmetler/bulut', color: 'indigo' },
  { icon: <HiOutlineBriefcase size={32} />, title: 'Danışmanlık', tag: 'Consulting', desc: 'Stratejik BT danışmanlığı ile doğru teknoloji yatırımları.', path: '/hizmetler/danismanlik', color: 'teal' },
];

const dataCenters = [
  { name: 'EMUR DC-1 — İstanbul (Avrupa)', tier: 'Tier 3+' },
  { name: 'EMUR DC-2 — İstanbul (Anadolu)', tier: 'Tier 3' },
  { name: 'EMUR DC-3 — Ankara', tier: 'Tier 3' },
  { name: 'EMUR DC-4 — İzmir', tier: 'Tier 3' },
];

const clientLogos = [
  'TEB', 'Turkcell', 'THY', 'Garanti BBVA', 'Arçelik', 'Koç Holding',
  'Sabancı', 'Eczacıbaşı', 'Pegasus', 'Anadolu Efes', 'Vestel', 'Ford Otosan'
];

const avatarGradients = [
  'linear-gradient(135deg, #2563eb, #06b6d4)',
  'linear-gradient(135deg, #7c3aed, #ec4899)',
  'linear-gradient(135deg, #059669, #22d3ee)',
];

const testimonials = [
  { name: 'Ahmet Yılmaz', role: 'CTO, TechCorp', text: 'EMUR Innovation ile çalışmaya başladıktan sonra altyapı maliyetlerimiz %40 düştü. Türkiye merkezli veri merkezleri sayesinde KVKK uyumluluğumuz da tam olarak sağlandı.', rating: 5 },
  { name: 'Elif Kaya', role: 'IT Direktörü, FinPlus', text: 'Bulut geçişimizi 3 haftada tamamladılar. 7/24 destek ekibi ve proaktif izleme sistemi sayesinde sıfır kesinti ile çalışıyoruz.', rating: 5 },
  { name: 'Mehmet Demir', role: 'Genel Müdür, E-Ticaret A.Ş.', text: 'CDN ve WAF çözümleriyle site hızımız 3 kat arttı. Black Friday döneminde bile performans düşüşü yaşamadık.', rating: 5 },
];

const categoryIcons = {
  'Bulut Bilişim': <HiOutlineCloud size={36} />,
  'Güvenlik': <HiOutlineShieldCheck size={36} />,
  'DevOps': <HiOutlineCube size={36} />,
  'Uyumluluk': <HiOutlineDocumentText size={36} />,
  'Veritabanı': <HiOutlineCircleStack size={36} />,
  'Eğitim': <HiOutlineComputerDesktop size={36} />,
  'Teknoloji': <HiOutlineBolt size={36} />,
};

function HomePage() {
  const [blogPosts, setBlogPosts] = useState([]);

  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        const res = await fetch('/api/blog');
        const data = await res.json();
        if (data.success) setBlogPosts(data.data.slice(0, 4));
      } catch (err) { console.error('Blog çekme hatası:', err); }
    };
    fetchBlogs();
    const interval = setInterval(fetchBlogs, 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="hero">
        <div className="hero-grid-bg" />
        <div className="hero-particles">
          {[...Array(6)].map((_, i) => <div className="hero-particle" key={i} />)}
        </div>
        <div className="container">
          <div className="hero-split">
            <div className="hero-content">
              <div className="hero-badge">
                <span className="badge-dot" />
                Türkiye'nin Yeni Nesil Bulut Entegratörü
              </div>
              <h1>
                İşletmenizi <span className="gradient-text">Buluta</span> Taşıyoruz,
                Geleceği Birlikte İnşa Ediyoruz
              </h1>
              <p className="hero-description">
                İstanbul, Ankara ve İzmir'deki veri merkezlerimizden 300'ün üzerinde firmaya bulut çözümleri üreterek
                BT sistemlerini üst seviyeye taşıyoruz.
              </p>
              <div className="hero-actions">
                <Link to="/hizmetler/bulut" className="btn btn-primary">Hemen Keşfet →</Link>
                <Link to="/iletisim" className="btn btn-secondary">İletişime Geç</Link>
              </div>
              <div className="hero-stats">
                <div className="hero-stat"><span className="stat-value">300+</span><span className="stat-label">Kurumsal Müşteri</span></div>
                <div className="hero-stat"><span className="stat-value">3</span><span className="stat-label">Büyükşehirde Hizmet</span></div>
                <div className="hero-stat"><span className="stat-value">4</span><span className="stat-label">Veri Merkezi</span></div>
                <div className="hero-stat"><span className="stat-value">%99.9</span><span className="stat-label">Uptime SLA</span></div>
              </div>
            </div>
            <div className="hero-visual">
              <img src="/hero-illustration.png" alt="EMUR Cloud Infrastructure" className="hero-image" />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR — CLIENT LOGOS */}
      <section className="trust-bar">
        <div className="container">
          <p className="trust-label">300'ün üzerinde firma güveniyor</p>
          <div className="trust-logos">
            <div className="trust-logos-track">
              {[...clientLogos, ...clientLogos].map((name, idx) => (
                <div className="trust-logo-item" key={idx}>
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* QUICK SERVICES BAR */}
      <div className="quick-services">
        <div className="container">
          <div className="quick-services-inner">
            <Link to="/hizmetler/bulut" className="quick-service-item active">
              <span className="qs-icon"><HiOutlineCloud size={18} /></span> Bulut Hizmetleri
            </Link>
            <Link to="/hizmetler/dijital-donusum" className="quick-service-item">
              <span className="qs-icon"><HiOutlineArrowPath size={18} /></span> Dijital Dönüşüm
            </Link>
            <Link to="/hizmetler/emurone" className="quick-service-item">
              <span className="qs-icon"><HiOutlineCube size={18} /></span> EmurOne
            </Link>
            <Link to="/hizmetler/hpc" className="quick-service-item">
              <span className="qs-icon"><HiOutlineBolt size={18} /></span> HPC
            </Link>
          </div>
        </div>
      </div>

      {/* SOLUTIONS */}
      <RevealSection>
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div className="section-label">Çözümlerimiz</div>
              <h2 className="section-title">Türkiye Genelinde Güçlü Çözümler</h2>
              <p className="section-subtitle">İstanbul, Ankara ve İzmir'deki uzman kadromuzla işletmenize en uygun BT çözümlerini sunuyoruz.</p>
            </div>
            <div className="solutions-grid">
              {solutions.map((item, idx) => (
                <Link to={item.path} className="solution-card" key={idx} data-color={item.color}>
                  <div className="solution-icon">{item.icon}</div>
                  <h3>{item.title}</h3>
                  <p>{item.desc}</p>
                  <span className="solution-link">İncele →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* CTA Banner */}
      <RevealSection>
        <section className="cta-section">
          <div className="container">
            <h2>Çözümlerimiz hakkında daha fazla bilgi almak ister misiniz?</h2>
            <p>İstanbul, Ankara ve İzmir ofislerimizden size en uygun çözümü sunmak için hazırız.</p>
            <Link to="/iletisim" className="btn btn-white">Sizi Arayalım →</Link>
          </div>
        </section>
      </RevealSection>

      {/* PROCESS */}
      <RevealSection>
        <section className="section process-section">
          <div className="container">
            <div className="section-header">
              <div className="section-label">Çalışma Sürecimiz</div>
              <h2 className="section-title">Alanında Uzman Ekip. Yenilikçi Çözümler.</h2>
            </div>
            <div className="process-grid">
              {processSteps.map((step, idx) => (
                <div className="process-card" key={idx}>
                  <div className="process-number">{step.num}</div>
                  <div className="process-icon">{step.icon}</div>
                  <h3>{step.title}</h3>
                  <p>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* FEATURED SERVICES */}
      <RevealSection>
        <section className="section featured-services-section">
          <div className="container">
            <div className="section-header">
              <div className="section-label">Öne Çıkan Hizmetler</div>
              <h2 className="section-title">Müşterilerimiz için işleri kolaylaştırıyoruz</h2>
              <p className="section-subtitle">İşletmenizin ihtiyaçlarına özel, uçtan uca yönetilen BT hizmetleri</p>
            </div>
            <div className="featured-grid">
              {featuredServices.map((service, idx) => (
                <Link to={service.path} className="featured-card" key={idx} data-color={service.color}>
                  <div className="featured-card-icon-wrap">
                    <div className="featured-card-icon">
                      {service.icon}
                    </div>
                    <div className="featured-card-glow" />
                  </div>
                  <div className="featured-card-content">
                    <span className="featured-card-tag">{service.tag}</span>
                    <h3>{service.title}</h3>
                    <p>{service.desc}</p>
                  </div>
                  <div className="featured-card-arrow">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M5 10h10m0 0-4-4m4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* TESTIMONIALS */}
      <RevealSection>
        <section className="section testimonials-section">
          <div className="container">
            <div className="section-header">
              <div className="section-label">Müşteri Yorumları</div>
              <h2 className="section-title">Müşterilerimiz Ne Diyor?</h2>
              <p className="section-subtitle">300'ün üzerinde firma EMUR Innovation'a güveniyor</p>
            </div>
            <div className="testimonials-grid">
              {testimonials.map((t, idx) => (
                <div className="testimonial-card" key={idx}>
                  <div className="testimonial-stars">
                    {[...Array(t.rating)].map((_, i) => <HiOutlineStar key={i} size={16} className="star-filled" />)}
                  </div>
                  <p className="testimonial-text">"{t.text}"</p>
                  <div className="testimonial-author">
                    <div className="author-avatar" style={{ background: avatarGradients[idx % avatarGradients.length] }}>{t.name.split(' ').map(n => n[0]).join('')}</div>
                    <div>
                      <div className="author-name">{t.name}</div>
                      <div className="author-role">{t.role}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>

      {/* DATA CENTERS */}
      <RevealSection>
        <section className="section global-section">
          <div className="container">
            <div className="global-content">
              <div>
                <div className="section-label" style={{ justifyContent: 'flex-start' }}>Türkiye Altyapısı</div>
                <h2 className="section-title" style={{ textAlign: 'left' }}>3 Büyükşehirde, 4 Veri Merkezi</h2>
                <p className="section-subtitle" style={{ textAlign: 'left', margin: 0 }}>
                  EMUR INNOVATION, İstanbul, Ankara ve İzmir'deki Tier 3+ sertifikalı veri merkezlerinde KVKK uyumlu hizmetler sunmaktadır.
                </p>
                <div className="global-stats">
                  <div className="global-stat-card"><div className="stat-value">3 Şehir</div><div className="stat-label">İstanbul, Ankara, İzmir</div></div>
                  <div className="global-stat-card"><div className="stat-value">4 DC</div><div className="stat-label">Tier 3+ Veri Merkezi</div></div>
                  <div className="global-stat-card"><div className="stat-value">400+ Gbps</div><div className="stat-label">Uplink Kapasitesi</div></div>
                  <div className="global-stat-card"><div className="stat-value">%100</div><div className="stat-label">KVKK Uyumlu</div></div>
                </div>
              </div>
              <div>
                <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '20px', color: 'rgba(255,255,255,0.8)' }}>Veri Merkezlerimiz</h3>
                <div className="dc-list">
                  {dataCenters.map((dc, idx) => (
                    <div className="dc-item" key={idx}>
                      <span className="dc-name"><HiOutlineMapPin size={16} style={{ marginRight: 6 }} /> {dc.name}</span>
                      <span className="dc-tier">{dc.tier}</span>
                    </div>
                  ))}
                </div>
                {/* Certifications */}
                <div className="cert-badges">
                  <div className="cert-badge">
                    <HiOutlineShieldCheck size={20} />
                    <span>ISO 27001</span>
                  </div>
                  <div className="cert-badge">
                    <HiOutlineDocumentText size={20} />
                    <span>KVKK Uyumlu</span>
                  </div>
                  <div className="cert-badge">
                    <HiOutlineShieldCheck size={20} />
                    <span>Tier 3+</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </RevealSection>

      {/* BLOG */}
      <RevealSection>
        <section className="section">
          <div className="container">
            <div className="section-header">
              <div className="section-label">Blog</div>
              <h2 className="section-title">Güncel Teknoloji İçerikleri</h2>
              <p className="section-subtitle">Yeni teknolojiler, çözümler ve sektörel gelişmeler</p>
            </div>
            <div className="blog-grid">
              {blogPosts.map((post, idx) => (
                <a href={post.link} target="_blank" rel="noopener noreferrer" className="blog-card" key={post.id || idx}>
                  <div className="blog-card-image" style={post.image ? { backgroundImage: `url(${post.image})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
                    {!post.image && (categoryIcons[post.category] || <HiOutlineBolt size={36} />)}
                    <span className="blog-category">{post.category}</span>
                  </div>
                  <div className="blog-card-body">
                    <span className="blog-date">{post.date}</span>
                    <h3 dangerouslySetInnerHTML={{ __html: post.title }} />
                    <span className="blog-link">Devamını Oku →</span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>
      </RevealSection>
    </>
  );
}

export default HomePage;
